import React, { useRef, useState } from "react";
import {
    STRIPE_EXPRESS_DEFAULT_ACCENT_COLOR,
    STRIPE_EXPRESS_DEFAULT_BRAND_COLOR,
} from "@common/constants/domains/stripe/stripe-express-branding";
import { StripeExpressBranding } from "@common/schemas/domains/stripe/stripe-express.schema";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ColorPickerControl } from "@design-system/ColorPickerControl/ColorPickerControl";
import { HStack } from "@design-system/HStack/HStack";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { toBase64 } from "@helpers/file";
import { InfoIcon } from "@icons";
import { AccountDetailsField } from "./AccountDetailsField";
import {
    buildStripeExpressBrandingUpdate,
    logoDraftAfterRemove,
    stripeExpressLogoDimensionsError,
    stripeExpressLogoPreviewUrl,
    StripeExpressBrandingDraft,
    validateStripeExpressLogoFile,
} from "./stripeExpressBrandingDraft";
import styles from "./StripeExpressBrandingSection.module.scss";

const LOGO_TOOLTIP =
    "The full-sized version of your logo. It must be at least 128px by 128px with a max size of 512KB. The supported MIME types are JPG and PNG.";
const BRAND_COLOR_TOOLTIP = "Add a splash of color to your branded pages";
const ACCENT_COLOR_TOOLTIP = "Used for the main button color across pages";

interface Props {
    branding: StripeExpressBranding;
    draft: StripeExpressBrandingDraft;
    isSaving: boolean;
    onDraftChange: (patch: Partial<StripeExpressBrandingDraft>) => void;
    onSave: (draft: StripeExpressBrandingDraft) => void;
    onLogoError: (message: string) => void;
}

export const StripeExpressBrandingFields: React.FC<Props> = ({
    branding,
    draft,
    isSaving,
    onDraftChange,
    onSave,
    onLogoError,
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isReadingLogo, setIsReadingLogo] = useState(false);
    const isLogoLocked = isSaving || isReadingLogo;
    const update = buildStripeExpressBrandingUpdate({
        saved: branding,
        draft,
    });
    const hasChanges = Object.keys(update).length > 0;
    const previewUrl = stripeExpressLogoPreviewUrl({
        savedLogoUrl: branding.logoUrl,
        logo: draft.logo,
    });

    const handleFileChange = async (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) {
            return;
        }
        const fileCheck = validateStripeExpressLogoFile({
            type: file.type,
            size: file.size,
        });
        if ("message" in fileCheck) {
            onLogoError(fileCheck.message);
            return;
        }
        setIsReadingLogo(true);
        try {
            const dimensionsError = stripeExpressLogoDimensionsError(
                await readLogoDimensions(file),
            );
            if (dimensionsError) {
                onLogoError(dimensionsError);
                return;
            }
            const data = await toBase64(file);
            onDraftChange({
                logo: {
                    status: "replaced",
                    data,
                    mimeType: fileCheck.mimeType,
                },
            });
        } catch {
            onLogoError("Couldn't read that logo");
        } finally {
            setIsReadingLogo(false);
        }
    };

    return (
        <main>
            <AccountDetailsField
                title={<FieldTitle label="Logo" tooltip={LOGO_TOOLTIP} />}
            >
                <HStack gap={12} align="center">
                    {previewUrl ? (
                        <img
                            src={previewUrl}
                            alt="Account logo"
                            className={styles.logo}
                        />
                    ) : null}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg"
                        className={styles.fileInput}
                        onChange={(event) => {
                            void handleFileChange(event);
                        }}
                    />
                    <Button
                        variant="secondary"
                        size="xs"
                        disabled={isLogoLocked}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Upload logo
                    </Button>
                    {previewUrl ? (
                        <Button
                            variant="secondary"
                            size="xs"
                            disabled={isLogoLocked}
                            onClick={() =>
                                onDraftChange({
                                    logo: logoDraftAfterRemove(draft.logo),
                                })
                            }
                        >
                            Remove
                        </Button>
                    ) : null}
                </HStack>
            </AccountDetailsField>
            <ColorField
                title="Brand color"
                tooltip={BRAND_COLOR_TOOLTIP}
                value={draft.brandColor}
                defaultColor={STRIPE_EXPRESS_DEFAULT_BRAND_COLOR}
                isDisabled={isSaving}
                onChange={(brandColor) => onDraftChange({ brandColor })}
            />
            <ColorField
                title="Accent color"
                tooltip={ACCENT_COLOR_TOOLTIP}
                value={draft.accentColor}
                defaultColor={STRIPE_EXPRESS_DEFAULT_ACCENT_COLOR}
                isDisabled={isSaving}
                onChange={(accentColor) => onDraftChange({ accentColor })}
            />
            <AccountDetailsField>
                <ButtonWithLoader
                    variant="primary"
                    size="sm"
                    loading={isSaving}
                    disabled={!hasChanges || isReadingLogo}
                    onClick={() => onSave(draft)}
                >
                    Save branding
                </ButtonWithLoader>
            </AccountDetailsField>
        </main>
    );
};

interface ColorFieldProps {
    title: string;
    tooltip: string;
    value: string | null;
    defaultColor: string;
    isDisabled: boolean;
    onChange: (value: string | null) => void;
}

const ColorField: React.FC<ColorFieldProps> = ({
    title,
    tooltip,
    value,
    defaultColor,
    isDisabled,
    onChange,
}) => (
    <AccountDetailsField title={<FieldTitle label={title} tooltip={tooltip} />}>
        <div className="account-details__field__control">
            <div className={styles.colorField}>
                <ColorPickerControl
                    value={value ?? defaultColor}
                    onChange={onChange}
                    ariaLabel={title}
                    disabled={isDisabled}
                    clearable={value !== null}
                    onClear={() => onChange(null)}
                />
            </div>
        </div>
    </AccountDetailsField>
);

const FieldTitle: React.FC<{ label: string; tooltip: string }> = ({
    label,
    tooltip,
}) => (
    <HStack gap={4} align="center">
        <span>{label}</span>
        <Tooltip content={tooltip}>
            <button
                type="button"
                aria-label={`About ${label.toLowerCase()}`}
                className={styles.labelTooltip}
            >
                <InfoIcon aria-hidden />
            </button>
        </Tooltip>
    </HStack>
);

async function readLogoDimensions(
    file: File,
): Promise<{ width: number; height: number }> {
    const bitmap = await createImageBitmap(file);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
}
